import React from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';


import {Skeleton} from 'src/components/skeleton';
import {ACCOUNT_TABS, UserSearchStateCriteria} from 'src/constants';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useConferenceAccountCheck} from 'src/hooks/useConferenceAccountCheck';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';

import Button from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';

import {UsersCountItem} from './users-count-item';
import {UsersSectionSkeleton} from './users-section-skeleton';
import {VirtualUsersCountItem} from './virtual-users-count-item';

type Props = {
    accountId: number,
};

export const UsersSection: React.FC<Props> = ({accountId}) => {
    const {users} = useSelector(selectAccountDetails);
    const {canCreateUser} = useAdminAccessRight();
    const navigate = useNavigate();
    const {userCreateOpen} = useEventTracking();

    const canCreate = canCreateUser(accountId);
    const isConferenceAccount = useConferenceAccountCheck();

    return (
        <Skeleton view={<UsersSectionSkeleton/>} shown={!users}>
            <FormSection
                headline="Users"
                info={headline => (
                    <Item grow={1}>
                        <Group justify="space-between">
                            {headline}
                            {canCreate && !isConferenceAccount ? (
                                <Button
                                    variant="Type.SECONDARY"
                                    onClick={() => {
                                        userCreateOpen({place: 'Sidebar'});
                                        navigate(`/profile/account/${accountId}/${ACCOUNT_TABS.CREATE_USER.name}`);
                                    }}>
                                    Add User
                                </Button>
                            ) : null}
                        </Group>
                    </Item>
                )}>
                {users ? (
                    <Group justify="space-between" alignItems="start">
                        <Group direction="v" gap="xs">
                            <UsersCountItem userType={UserSearchStateCriteria.LICENSED} value={users.licensed}/>
                            <UsersCountItem userType={UserSearchStateCriteria.MOBILE} value={users.mobile}/>
                            <UsersCountItem userType={UserSearchStateCriteria.CRM_ONLY} value={users.crmOnly}/>
                            <UsersCountItem
                                userType={UserSearchStateCriteria.CREDIT_ALERTS_ONLY}
                                value={users.creditAlerts}
                            />
                        </Group>
                        <Group direction="v" gap="xs">
                            <UsersCountItem userType={UserSearchStateCriteria.FREE_NEWSLETTER} value={users.disabled}/>
                            <UsersCountItem userType={UserSearchStateCriteria.NO_ACCESS} value={users.noAccess}/>
                            <VirtualUsersCountItem value={users.virtual}/>
                        </Group>
                    </Group>
                ) : null}
            </FormSection>
        </Skeleton>
    );
};
